import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/App";

const FLUSH_INTERVAL = 15000; // 15s
const PAGE_NAMES = {
  "/": "Insights",
  "/costs": "Cost Intelligence",
  "/resources": "Resources",
  "/changes": "Changes",
  "/cleanup": "Cleanup Wizard",
  "/system": "System",
  "/billing": "Billing",
  "/admin": "Admin",
  "/discovery": "Discovery Feed",
  "/docs": "Documentation",
};

let eventQueue = [];
let pageEnterTime = null;

export function useAnalytics() {
  const { api, user } = useAuth();
  const location = useLocation();
  const prevPath = useRef(null);
  const flushTimer = useRef(null);

  const flush = useCallback(async () => {
    if (!eventQueue.length || !user) return;
    const batch = [...eventQueue];
    eventQueue = [];
    try {
      await api().post("/analytics/batch", { events: batch });
    } catch {
      // silently fail - analytics should never block UX
    }
  }, [api, user]);

  // Track page views
  useEffect(() => {
    if (!user) return;
    const path = location.pathname;
    const now = Date.now();

    // Log dwell time for previous page
    if (prevPath.current && pageEnterTime) {
      eventQueue.push({
        event_type: "page_view",
        page: PAGE_NAMES[prevPath.current] || prevPath.current,
        duration_ms: now - pageEnterTime,
      });
    }

    prevPath.current = path;
    pageEnterTime = now;
  }, [location.pathname, user]);

  // Periodic flush
  useEffect(() => {
    if (!user) return;
    flushTimer.current = setInterval(flush, FLUSH_INTERVAL);
    const handleUnload = () => {
      if (prevPath.current && pageEnterTime) {
        eventQueue.push({
          event_type: "page_view",
          page: PAGE_NAMES[prevPath.current] || prevPath.current,
          duration_ms: Date.now() - pageEnterTime,
        });
      }
      // Use sendBeacon for reliability on unload
      if (eventQueue.length && navigator.sendBeacon) {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/analytics/batch`;
        const token = localStorage.getItem("token");
        const blob = new Blob([JSON.stringify({ events: eventQueue })], { type: "application/json" });
        // sendBeacon doesn't support custom headers, so we'll flush normally
        eventQueue = [];
      }
      flush();
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      clearInterval(flushTimer.current);
      window.removeEventListener("beforeunload", handleUnload);
      flush();
    };
  }, [user, flush]);

  const trackFeature = useCallback((feature, metadata) => {
    if (!user) return;
    eventQueue.push({
      event_type: "feature_click",
      feature,
      page: PAGE_NAMES[window.location.pathname] || window.location.pathname,
      metadata: metadata || {},
    });
  }, [user]);

  const trackAction = useCallback((action, metadata) => {
    if (!user) return;
    eventQueue.push({
      event_type: "action",
      feature: action,
      page: PAGE_NAMES[window.location.pathname] || window.location.pathname,
      metadata: metadata || {},
    });
  }, [user]);

  return { trackFeature, trackAction };
}
